import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Stack,
  Table,
  Textarea,
  Typography,
} from '@mui/joy';
import {
  BILL_PAYMENT_TERMS_LABELS,
  CIS_BAND_LABELS,
  accountMatchesSearch,
  validateAccountInput,
  type BillPaymentTerms,
  type CisBand,
  type DirectoryAccount,
  type DirectoryAccountInput,
} from '../accounts.model';
import { accountsService } from '../accounts.service';

const CIS_BANDS = Object.keys(CIS_BAND_LABELS) as CisBand[];
const BILL_PAYMENT_TERMS_OPTIONS = Object.keys(BILL_PAYMENT_TERMS_LABELS) as BillPaymentTerms[];

const EMPTY_INPUT: DirectoryAccountInput = {
  companyName: '',
  contactName: '',
  email: '',
  billingEmail: '',
  phone: '',
  address: '',
  vatRegistered: false,
  vatNumber: '',
  cisBand: 'none',
  utrNumber: '',
  cisVerificationNumber: '',
  notes: '',
  isActive: true,
  isKeystoneSubcontractor: false,
  keystoneEmail: '',
  billPaymentTerms: 'none',
  niNumber: '',
  bankName: '',
  bankSortCode: '',
  bankAccountNumber: '',
  bankAccountName: '',
  logoUrl: '',
};

function inputFrom(account: DirectoryAccount): DirectoryAccountInput {
  return {
    companyName: account.companyName,
    contactName: account.contactName ?? '',
    email: account.email ?? '',
    billingEmail: account.billingEmail ?? '',
    phone: account.phone ?? '',
    address: account.address ?? '',
    vatRegistered: account.vatRegistered,
    vatNumber: account.vatNumber ?? '',
    cisBand: account.cisBand,
    utrNumber: account.utrNumber ?? '',
    cisVerificationNumber: account.cisVerificationNumber ?? '',
    notes: account.notes ?? '',
    isActive: account.isActive,
    isKeystoneSubcontractor: account.isKeystoneSubcontractor,
    keystoneEmail: account.keystoneEmail ?? '',
    billPaymentTerms: account.billPaymentTerms,
    niNumber: account.niNumber ?? '',
    bankName: account.bankName ?? '',
    bankSortCode: account.bankSortCode ?? '',
    bankAccountNumber: account.bankAccountNumber ?? '',
    bankAccountName: account.bankAccountName ?? '',
    logoUrl: account.logoUrl ?? '',
  };
}

export function AccountFormModal({
  account,
  open,
  onClose,
  onSaved,
  onDeleted,
}: {
  account: DirectoryAccount | null;
  open: boolean;
  onClose: () => void;
  onSaved: (account: DirectoryAccount) => void;
  /** Shows a Delete button when editing. The database refuses accounts that have invoices, bills or job cards. */
  onDeleted?: (accountId: string) => void;
}) {
  const [input, setInput] = useState<DirectoryAccountInput>(EMPTY_INPUT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setInput(account ? inputFrom(account) : EMPTY_INPUT);
    setError(null);
    setConfirmDelete(false);
  }, [account, open]);

  const remove = async () => {
    if (!account || !onDeleted) return;
    setDeleting(true);
    setError(null);
    try {
      await accountsService.remove(account.id);
      onDeleted(account.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete the account');
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const set = <K extends keyof DirectoryAccountInput>(key: K, value: DirectoryAccountInput[K]) => {
    setInput((current) => ({ ...current, [key]: value }));
  };
  const text = (key: keyof DirectoryAccountInput) => ({
    value: String(input[key] ?? ''),
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => set(key, event.target.value as never),
  });

  const save = async () => {
    const problem = validateAccountInput(input);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = account
        ? await accountsService.update(account.id, input)
        : await accountsService.create(input);
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the account');
    } finally {
      setSaving(false);
    }
  };

  const isCis = input.cisBand && input.cisBand !== 'none';

  return (
    <Modal open={open} onClose={() => !saving && onClose()}>
      <ModalDialog sx={{ width: { xs: '100%', sm: 640 }, maxHeight: '90vh', overflow: 'auto' }}>
        <DialogTitle>{account ? 'Edit account' : 'New account'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Grid container spacing={1.5}>
              <Grid xs={12}>
                <FormControl required>
                  <FormLabel>Company or trading name</FormLabel>
                  <Input {...text('companyName')} autoFocus />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Contact name</FormLabel>
                  <Input {...text('contactName')} />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Phone</FormLabel>
                  <Input {...text('phone')} />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input type="email" {...text('email')} />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Billing email</FormLabel>
                  <Input type="email" {...text('billingEmail')} />
                  <FormHelperText>Invoices go here first.</FormHelperText>
                </FormControl>
              </Grid>
              <Grid xs={12}>
                <FormControl>
                  <FormLabel>Address</FormLabel>
                  <Textarea minRows={2} {...text('address')} />
                </FormControl>
              </Grid>
              <Grid xs={12}>
                <Typography level="title-sm">Tax details</Typography>
                <Typography level="body-xs" textColor="neutral.600">
                  Facts about this company. Each arrangement sets its own VAT and CIS; the CIS status here pre-fills their bills to us.
                </Typography>
              </Grid>
              <Grid xs={12} sm={5}>
                <Checkbox
                  label="VAT registered"
                  checked={!!input.vatRegistered}
                  onChange={(event) => set('vatRegistered', event.target.checked)}
                  sx={{ mt: { sm: 3.5 } }}
                />
              </Grid>
              <Grid xs={12} sm={7}>
                <FormControl disabled={!input.vatRegistered}>
                  <FormLabel>VAT number</FormLabel>
                  <Input {...text('vatNumber')} />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={4}>
                <FormControl>
                  <FormLabel>CIS status</FormLabel>
                  <Select value={input.cisBand} onChange={(_, value) => value && set('cisBand', value as CisBand)}>
                    {CIS_BANDS.map((band) => <Option key={band} value={band}>{CIS_BAND_LABELS[band]}</Option>)}
                  </Select>
                  <FormHelperText>When they bill us.</FormHelperText>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={4}>
                <FormControl>
                  <FormLabel>UTR</FormLabel>
                  <Input {...text('utrNumber')} placeholder="10 digits" />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={4}>
                <FormControl disabled={!isCis}>
                  <FormLabel>Verification number</FormLabel>
                  <Input {...text('cisVerificationNumber')} placeholder="V1234567890" />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={5}>
                <Checkbox
                  label="Keystone subcontractor"
                  checked={!!input.isKeystoneSubcontractor}
                  onChange={(event) => set('isKeystoneSubcontractor', event.target.checked)}
                  sx={{ mt: { sm: 3.5 } }}
                />
              </Grid>
              <Grid xs={12} sm={7}>
                <FormControl disabled={!input.isKeystoneSubcontractor}>
                  <FormLabel>Keystone email</FormLabel>
                  <Input type="email" {...text('keystoneEmail')} placeholder="name@keystonesouthuk.co.uk" />
                  <FormHelperText>The address they sign in to JARVIS with. They see their own CIS certificates once each tax month ends.</FormHelperText>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={7}>
                <FormControl>
                  <FormLabel>We pay their bills</FormLabel>
                  <Select
                    value={input.billPaymentTerms || 'none'}
                    onChange={(_, value) => value && set('billPaymentTerms', value as BillPaymentTerms)}
                  >
                    {BILL_PAYMENT_TERMS_OPTIONS.map((terms) => <Option key={terms} value={terms}>{BILL_PAYMENT_TERMS_LABELS[terms]}</Option>)}
                  </Select>
                  <FormHelperText>Fills in the due date on their new bills.</FormHelperText>
                </FormControl>
              </Grid>
              <Grid xs={12}>
                <Typography level="title-sm">Subcontractor details</Typography>
                <Typography level="body-xs" textColor="neutral.600">
                  Printed on the bills we write up for them. Leave blank for suppliers who send their own invoices.
                </Typography>
              </Grid>
              <Grid xs={12} sm={4}>
                <FormControl>
                  <FormLabel>NI number</FormLabel>
                  <Input {...text('niNumber')} />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={8}>
                <FormControl>
                  <FormLabel>Logo</FormLabel>
                  <Input {...text('logoUrl')} placeholder="/subcontractor-logos/name.png" />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Bank</FormLabel>
                  <Input {...text('bankName')} />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Account name</FormLabel>
                  <Input {...text('bankAccountName')} />
                </FormControl>
              </Grid>
              <Grid xs={6}>
                <FormControl>
                  <FormLabel>Sort code</FormLabel>
                  <Input {...text('bankSortCode')} />
                </FormControl>
              </Grid>
              <Grid xs={6}>
                <FormControl>
                  <FormLabel>Account number</FormLabel>
                  <Input {...text('bankAccountNumber')} />
                </FormControl>
              </Grid>
              <Grid xs={12}>
                <FormControl>
                  <FormLabel>Notes</FormLabel>
                  <Textarea minRows={2} {...text('notes')} />
                </FormControl>
              </Grid>
              {account && (
                <Grid xs={12}>
                  <Checkbox
                    label="Active"
                    checked={input.isActive !== false}
                    onChange={(event) => set('isActive', event.target.checked)}
                  />
                </Grid>
              )}
            </Grid>
            {error && <Alert color="danger">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={save} loading={saving} disabled={deleting}>{account ? 'Save' : 'Create account'}</Button>
          <Button variant="plain" color="neutral" onClick={onClose} disabled={saving || deleting}>Cancel</Button>
          {account && onDeleted && (
            confirmDelete ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 'auto' }}>
                <Typography level="body-sm">Delete {account.companyName} permanently?</Typography>
                <Button color="danger" loading={deleting} onClick={() => { void remove(); }}>Delete</Button>
                <Button variant="plain" color="neutral" disabled={deleting} onClick={() => setConfirmDelete(false)}>Keep</Button>
              </Stack>
            ) : (
              <Button
                variant="plain"
                color="danger"
                disabled={saving}
                onClick={() => setConfirmDelete(true)}
                sx={{ mr: 'auto' }}
              >
                Delete account
              </Button>
            )
          )}
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}

/** Directory → Accounts: partner trades, tenants, suppliers and subcontractors we invoice or are billed by. */
const AccountsTab: React.FC<{ onCountChange?: (count: number) => void }> = ({ onCountChange }) => {
  const [accounts, setAccounts] = useState<DirectoryAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<DirectoryAccount | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await accountsService.list();
      setAccounts(rows);
      onCountChange?.(rows.filter((row) => row.isActive).length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => accounts.filter((account) => (
    (showInactive || account.isActive)
    && accountMatchesSearch(account, search)
  )), [accounts, search, showInactive]);

  const openForm = (account: DirectoryAccount | null) => {
    setEditing(account);
    setFormOpen(true);
  };

  const onDeleted = (accountId: string) => {
    setFormOpen(false);
    setAccounts((current) => {
      const next = current.filter((row) => row.id !== accountId);
      onCountChange?.(next.filter((row) => row.isActive).length);
      return next;
    });
  };

  const onSaved = (saved: DirectoryAccount) => {
    setFormOpen(false);
    setAccounts((current) => {
      const next = current.some((row) => row.id === saved.id)
        ? current.map((row) => (row.id === saved.id ? saved : row))
        : [...current, saved];
      const sorted = next.sort((a, b) => a.companyName.localeCompare(b.companyName));
      onCountChange?.(sorted.filter((row) => row.isActive).length);
      return sorted;
    });
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <Input
          placeholder="Search accounts"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ flex: 1 }}
        />
        <Checkbox label="Show inactive" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} />
        <Button onClick={() => openForm(null)}>New account</Button>
      </Stack>

      {error && <Alert color="danger">{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
          <Table hoverRow sx={{ minWidth: 760, tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ width: '26%' }}>Account</th>
                <th style={{ width: '26%' }}>Email</th>
                <th style={{ width: '11%' }}>VAT</th>
                <th style={{ width: '14%' }}>CIS</th>
                <th style={{ width: '13%' }}>Keystone sub</th>
                <th style={{ width: '10%' }}>FreeAgent</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((account) => (
                <tr key={account.id} onClick={() => openForm(account)} style={{ cursor: 'pointer' }}>
                  <td>
                    <Typography level="body-sm" fontWeight="lg">
                      {account.companyName}
                      {account.isKeystoneSubcontractor && (
                        <Chip size="sm" variant="soft" color="primary" sx={{ ml: 1 }} title={account.keystoneEmail || 'No sign-in email yet'}>
                          Keystone
                        </Chip>
                      )}
                    </Typography>
                    {account.contactName && <Typography level="body-xs" textColor="neutral.500">{account.contactName}</Typography>}
                    {!account.isActive && <Chip size="sm" variant="outlined" color="neutral">Inactive</Chip>}
                  </td>
                  <td>
                    <Typography
                      level="body-sm"
                      title={account.billingEmail || account.email || undefined}
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {account.billingEmail || account.email || '—'}
                    </Typography>
                  </td>
                  <td><Typography level="body-sm">{account.vatRegistered ? (account.vatNumber || 'Registered') : 'No'}</Typography></td>
                  <td><Typography level="body-sm">{account.cisBand === 'none' ? '—' : CIS_BAND_LABELS[account.cisBand]}</Typography></td>
                  <td>
                    {account.isKeystoneSubcontractor ? (
                      <Chip size="sm" variant="soft" color="success" title={account.keystoneEmail || undefined}>Yes</Chip>
                    ) : (
                      <Typography level="body-sm">—</Typography>
                    )}
                  </td>
                  <td><Typography level="body-sm">{account.freeagentContactUrl ? 'Linked' : '—'}</Typography></td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <Typography level="body-sm" textColor="neutral.500" sx={{ py: 2, textAlign: 'center' }}>
                      {accounts.length === 0 ? 'No accounts yet.' : 'No accounts match your filters.'}
                    </Typography>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Sheet>
      )}

      <AccountFormModal
        account={editing}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={onSaved}
        onDeleted={onDeleted}
      />
    </Stack>
  );
};

export default AccountsTab;
